import type { Transfer } from '@0xtorch/core'
import type { CostBasis } from '../../types/costBasis'
import type { FeeSubCategory, Transaction } from '../../types/transaction'
import { mergeActionTransfers } from '../../utils/mergeActionTransfers'
import { createBlankTransaction } from './createBlankTransaction'
import { createFeeTransaction } from './createFeeTransaction'
import { createIncomeTransaction } from './createIncomeTransaction'
import { replaceBondOfTransfers } from './replaceBondOfTransfers'
import type {
  CreateTransactionsByCategoryActionParameters,
  CreateTransactionsByCategoryActionReturnTypes,
} from './types'
import { createInsufficientTransactionByTransfer } from './util/createInsufficientTransactionByTransfer.ts'

type CreateTransactionsByFeeActionParameters =
  CreateTransactionsByCategoryActionParameters & {
    readonly costBasis: CostBasis
    readonly use5PercentRule?: boolean
    readonly feeSubCategory?: FeeSubCategory
  }

type CreateTransactionsByFeeActionReturnTypes =
  CreateTransactionsByCategoryActionReturnTypes

export const createTransactionsByFeeAction = ({
  actionId,
  action,
  loans,
  crossActionBundles,
  assetBalances,
  assetBorrows,
  usedBalances,
  costBasis,
  isLocked = false,
  use5PercentRule = false,
  feeSubCategory,
}: CreateTransactionsByFeeActionParameters): CreateTransactionsByFeeActionReturnTypes => {
  if (isLocked) {
    return {
      transactions: [],
      loans,
      crossActionBundles,
      assetBalances,
      assetBorrows,
      usedBalances,
    }
  }

  // merge transfers & replace bond of transfers
  const mergedTransfers = mergeActionTransfers(action.transfers)
  const handleBondResult = replaceBondOfTransfers({
    actionId,
    timestamp: action.timestamp,
    transfers: mergedTransfers,
    loans,
    assetBalances,
    assetBorrows,
    usedBalances,
  })
  const newLoans = handleBondResult.loans
  let newAssetBalances = handleBondResult.assetBalances
  let newAssetBorrows = handleBondResult.assetBorrows
  let newUsedBalances = handleBondResult.usedBalances
  const transactions: Transaction[] = [...handleBondResult.transactions]
  const transfers = handleBondResult.transfers
  const transferCount: { all: number; in: number; out: number } = {
    all: 0,
    in: 0,
    out: 0,
  }
  const inTransfers: Transfer[] = []
  const outTransfers: Transfer[] = []
  for (const transfer of transfers) {
    transferCount.all += 1
    if (transfer.direction === 'in') {
      transferCount.in += 1
      inTransfers.push(transfer)
    }
    if (transfer.direction === 'out') {
      transferCount.out += 1
      outTransfers.push(transfer)
    }
  }

  if (transferCount.all === 0) {
    const result = createBlankTransaction({
      actionId,
      timestamp: action.timestamp,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
    return {
      transactions,
      loans: newLoans,
      crossActionBundles,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    }
  }

  for (const transfer of inTransfers) {
    const result = createIncomeTransaction({
      actionId,
      timestamp: action.timestamp,
      subCategory: 'other-income',
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
      costBasis,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
  }

  for (const transfer of outTransfers) {
    // 不足する場合は insufficient 取引を追加
    const insufficientResult = createInsufficientTransactionByTransfer({
      actionId,
      timestamp: action.timestamp,
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
      costBasis,
    })
    if (insufficientResult !== undefined) {
      transactions.push(insufficientResult.transaction)
      newAssetBalances = insufficientResult.assetBalances
      newAssetBorrows = insufficientResult.assetBorrows
      newUsedBalances = insufficientResult.usedBalances
    }

    const result = createFeeTransaction({
      actionId,
      timestamp: action.timestamp,
      subCategory: feeSubCategory,
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
      costBasis,
      use5PercentRule,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
  }

  return {
    transactions,
    loans: newLoans,
    crossActionBundles,
    assetBalances: newAssetBalances,
    assetBorrows: newAssetBorrows,
    usedBalances: newUsedBalances,
  }
}
